/**
 * Supabase Authentication Service
 * 
 * Provides methods for interacting with Supabase authentication.
 * This is a low-level service used by the authentication context.
 */

import { supabase } from './supabase';
import { Session, User, AuthError } from '@supabase/supabase-js';

/**
 * Get the current session from Supabase
 * 
 * @returns Promise resolving to the current session
 */
export async function getSession(): Promise<{ 
  session: Session | null; 
  error: AuthError | null;
}> {
  try {
    const { data, error } = await supabase.auth.getSession();
    return { session: data.session, error };
  } catch (error) {
    console.error('Unexpected error getting session:', error);
    return { session: null, error: error as AuthError };
  }
}

/**
 * Get the current user from Supabase
 * 
 * @returns Promise resolving to the current user
 */
export async function getUser(): Promise<{ 
  user: User | null; 
  error: AuthError | null;
}> {
  try {
    const { data, error } = await supabase.auth.getUser();
    return { user: data.user, error };
  } catch (error) {
    console.error('Unexpected error getting user:', error);
    return { user: null, error: error as AuthError };
  }
}

/**
 * Sign in with email and password
 * 
 * @param email - User email
 * @param password - User password
 * @returns Promise resolving to the session and user
 */
export async function signInWithPassword(email: string, password: string): Promise<{
  session: Session | null;
  user: User | null;
  error: AuthError | null;
}> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { 
      session: data?.session || null, 
      user: data?.user || null, 
      error 
    };
  } catch (error) {
    console.error('Unexpected error during sign in:', error);
    return { 
      session: null, 
      user: null, 
      error: error as AuthError 
    };
  }
}

/**
 * Sign up with email and password
 * 
 * @param email - User email
 * @param password - User password
 * @returns Promise resolving to the session and user
 */
export async function signUp(email: string, password: string): Promise<{
  session: Session | null;
  user: User | null;
  error: AuthError | null;
}> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    return { 
      session: data?.session || null, 
      user: data?.user || null, 
      error 
    };
  } catch (error) {
    console.error('Unexpected error during sign up:', error);
    return { 
      session: null, 
      user: null, 
      error: error as AuthError 
    };
  }
}

/**
 * Sign out the current user
 * 
 * @returns Promise resolving to success or error
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signOut();
    return { error };
  } catch (error) {
    console.error('Unexpected error during sign out:', error);
    return { error: error as AuthError };
  }
}

/**
 * Reset password for an email
 * 
 * @param email - User email
 * @returns Promise resolving to success or error
 */
export async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    
    return { error };
  } catch (error) {
    console.error('Unexpected error during password reset:', error);
    return { error: error as AuthError };
  }
}

/**
 * Refresh the current session
 * 
 * @returns Promise resolving to the refreshed session
 */
export async function refreshSession(): Promise<{
  session: Session | null;
  error: AuthError | null;
}> {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    
    return { 
      session: data?.session || null, 
      error 
    };
  } catch (error) {
    console.error('Unexpected error refreshing session:', error);
    return { 
      session: null, 
      error: error as AuthError 
    };
  }
}

/**
 * Set up an auth state change listener
 * 
 * @param callback - Function to call when auth state changes
 * @returns Subscription object with unsubscribe method
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}
